import { useEffect, useState } from "react";
import { findVisibleTarget } from "@/lib/dom/findVisibleTarget";

/**
 * O passo mais adiantado de um roteiro cujo elemento já está na tela: `{ index, element }`, ou
 * `null` se nenhum está. `selectors` é a lista de passos na ordem (o último é o mais perto do
 * recurso); vazia desliga a busca.
 *
 * Mesma busca de `useVisibleElement`: repete quando o DOM muda ou a janela é redimensionada,
 * agrupada em um quadro, e só atualiza o estado quando o resultado mudou.
 *
 * @param {string[]} selectors
 * @returns {{ index: number, element: Element }|null}
 */
export function useVisibleStep(selectors) {
  const [reached, setReached] = useState(null);
  const signature = selectors.join("\n");

  useEffect(() => {
    const list = signature ? signature.split("\n") : [];
    if (list.length === 0) {
      setReached(null);
      return undefined;
    }
    let frame = 0;

    const resolve = () => {
      frame = 0;
      let next = null;
      for (let index = list.length - 1; index >= 0 && !next; index -= 1) {
        const element = findVisibleTarget(list[index]);
        if (element) next = { index, element };
      }
      setReached((previous) => (previous?.index === next?.index && previous?.element === next?.element ? previous : next));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(resolve);
    };

    resolve();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", schedule);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [signature]);

  return reached;
}
